import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { ConfirmDialog, ArchiveConfirmDialog } from './Modal';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { changeTaskStatus, deleteTask, fetchTasks } from '../tasksApi';
import { archiveEntity } from '../archiveApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';

export default function TasksPage() {
  const { language, direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [assigneeId, setAssigneeId] = useState('all');
  const [priority, setPriority] = useState('all');
  const [linkedType, setLinkedType] = useState('all');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [archiveConfirmId, setArchiveConfirmId] = useState(null);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setError(t('common.noPerm')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchTasks();
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('tasks.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => rows.filter((x) => {
    const qq = q.trim().toLowerCase();
    const byQ = !qq || String(x.title || '').toLowerCase().includes(qq) || String(x.assigneeName || '').toLowerCase().includes(qq);
    const byStatus = status === 'all' || x.status === status;
    const byAssignee = assigneeId === 'all' || String(x.assigneeBoardMemberId || '') === assigneeId;
    const byPriority = priority === 'all' || x.priority === priority;
    const byLink = linkedType === 'all'
      || (linkedType === 'meeting' && x.linkedMeetingId)
      || (linkedType === 'decision' && x.linkedDecisionId)
      || (linkedType === 'none' && !x.linkedMeetingId && !x.linkedDecisionId);
    return byQ && byStatus && byAssignee && byPriority && byLink;
  }), [rows, q, status, assigneeId, priority, linkedType]);

  const stats = useMemo(() => ({
    total: rows.length,
    completed: rows.filter((x) => x.status === 'completed').length,
    inProgress: rows.filter((x) => x.status === 'in_progress').length,
    overdue: rows.filter((x) => x.status === 'overdue').length,
  }), [rows]);

  const assignees = useMemo(() => {
    const m = new Map();
    rows.forEach((x) => { if (x.assigneeBoardMemberId) m.set(String(x.assigneeBoardMemberId), x.assigneeName || `#${x.assigneeBoardMemberId}`); });
    return Array.from(m.entries());
  }, [rows]);

  const statusLabel = (v) => {
    if (v === 'completed') return t('tasks.statusCompleted');
    if (v === 'in_progress') return t('tasks.statusInProgress');
    if (v === 'overdue') return t('tasks.statusOverdue');
    return t('tasks.statusNew');
  };
  const priorityLabel = (v) => {
    if (v === 'high') return t('tasks.priorityHigh');
    if (v === 'low') return t('tasks.priorityLow');
    return t('tasks.priorityMedium');
  };

  const dateLocale = (language === 'ar' || language === 'ur') ? 'ar-SA' : 'en-US';

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="settings" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('tasks.title')}</span>
            <span className="list-page-card__sub">{t('tasks.subtitle')}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--ghost" onClick={() => navigate(`${basePath}/archive?type=Task`)}><Icon name="archive" size={14} /> {t('nav.archive')}</button>
            <button type="button" className="btn btn--primary" onClick={() => navigate(`${basePath}/tasks/new`)}>{t('tasks.new')}</button>
          </div>
        </header>

        <div className="list-page-card__metrics">
          <div className="table-card" style={{ padding: 10 }}><div style={{ color: '#78909c', fontSize: '.78rem' }}>{t('tasks.total')}</div><div style={{ fontWeight: 800, fontSize: '1.2rem' }}>{stats.total}</div></div>
          <div className="table-card" style={{ padding: 10 }}><div style={{ color: '#78909c', fontSize: '.78rem' }}>{t('tasks.completed')}</div><div style={{ fontWeight: 800, fontSize: '1.2rem' }}>{stats.completed}</div></div>
          <div className="table-card" style={{ padding: 10 }}><div style={{ color: '#78909c', fontSize: '.78rem' }}>{t('tasks.inProgress')}</div><div style={{ fontWeight: 800, fontSize: '1.2rem' }}>{stats.inProgress}</div></div>
          <div className="table-card" style={{ padding: 10, borderColor: '#ffcdd2', background: '#fff8f8' }}><div style={{ color: '#c62828', fontSize: '.78rem' }}>{t('tasks.overdue')}</div><div style={{ fontWeight: 800, color: '#c62828', fontSize: '1.2rem' }}>{stats.overdue}</div></div>
        </div>

        <div className="page__filters">
        <div className="page__search"><Icon name="search" size={14} color="#90a4ae" className="page__search-icon" /><input className="page__search-input" placeholder={t('tasks.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select className="page__filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">{t('tasks.allStatuses')}</option>
          <option value="new">{t('tasks.statusNew')}</option>
          <option value="in_progress">{t('tasks.statusInProgress')}</option>
          <option value="completed">{t('tasks.statusCompleted')}</option>
          <option value="overdue">{t('tasks.statusOverdue')}</option>
        </select>
        <select className="page__filter-select" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
          <option value="all">{t('tasks.allAssignees')}</option>
          {assignees.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select className="page__filter-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="all">{t('tasks.allPriorities')}</option>
          <option value="high">{t('tasks.priorityHigh')}</option>
          <option value="medium">{t('tasks.priorityMedium')}</option>
          <option value="low">{t('tasks.priorityLow')}</option>
        </select>
        <select className="page__filter-select" value={linkedType} onChange={(e) => setLinkedType(e.target.value)}>
          <option value="all">{t('tasks.allLinks')}</option>
          <option value="meeting">{t('tasks.linkMeeting')}</option>
          <option value="decision">{t('tasks.linkDecision')}</option>
          <option value="none">{t('tasks.linkNone')}</option>
        </select>
        </div>

        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t('common.loading')}</div>}

      {!loading && (
        <div className="table-card">
          <table className="data-table">
            <thead><tr><th style={{ width: 42 }}>#</th><th>{t('tasks.colTitle')}</th><th>{t('tasks.assignee')}</th><th>{t('tasks.colPriority')}</th><th>{t('common.status')}</th><th>{t('tasks.colDue')}</th><th>{t('tasks.colLink')}</th><th>{t('common.actions')}</th></tr></thead>
            <tbody>
              {filtered.map((x, idx) => (
                <tr key={x.id}>
                  <td className="td-num">{idx + 1}</td>
                  <td><strong>{x.title}</strong></td>
                  <td>{x.assigneeName || '—'}</td>
                  <td>{priorityLabel(x.priority)}</td>
                  <td><span style={{ color: x.status === 'overdue' ? '#c62828' : '#455a64', fontWeight: 700 }}>{statusLabel(x.status)}</span></td>
                  <td>{x.dueDateUtc ? new Date(x.dueDateUtc).toLocaleDateString(dateLocale) : '—'}</td>
                  <td>{x.linkedMeetingTitle || x.linkedDecisionTitle || '—'}</td>
                  <td><div className="td-actions">
                    <button className="action-btn action-btn--view" onClick={() => navigate(`${basePath}/tasks/${x.id}`)}><Icon name="eye" size={14} /></button>
                    <button className="action-btn action-btn--edit" onClick={() => navigate(`${basePath}/tasks/${x.id}/edit`)}><Icon name="edit" size={14} /></button>
                    <button className="action-btn action-btn--archive" title={t('archive.title')} onClick={() => setArchiveConfirmId(x.id)}><Icon name="archive" size={14} /></button>
                    <button className="action-btn action-btn--delete" onClick={() => { setTarget(x); setConfirmOpen(true); }}><Icon name="trash" size={14} /></button>
                    <button className="action-btn action-btn--ghost" onClick={async () => { await changeTaskStatus(x.id, x.status === 'completed' ? 'in_progress' : 'completed'); setRows((p) => p.map((r) => (r.id === x.id ? { ...r, status: x.status === 'completed' ? 'in_progress' : 'completed' } : r))); }}>
                      <Icon name="check-circle" size={14} />
                    </button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="users-empty">{t('tasks.noRows')}</div>}
        </div>
      )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          if (!target) return;
          await deleteTask(target.id);
          setRows((p) => p.filter((r) => r.id !== target.id));
          setConfirmOpen(false);
        }}
        itemName={target?.title}
        title={t('tasks.confirmDelete')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
      />

      <ArchiveConfirmDialog
        open={!!archiveConfirmId}
        onClose={() => setArchiveConfirmId(null)}
        onConfirm={async () => {
          if (!archiveConfirmId) return;
          setArchiving(true);
          try {
            await archiveEntity('Task', archiveConfirmId);
            setRows((p) => p.filter((r) => r.id !== archiveConfirmId));
          } catch { /* ignore */ }
          setArchiving(false);
          setArchiveConfirmId(null);
        }}
        itemName={rows.find((r) => r.id === archiveConfirmId)?.title}
        title={t('archive.title')}
        confirmLabel={t('archive.title')}
        cancelLabel={t('common.cancel')}
        loading={archiving}
      />
    </div>
  );
}
